import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, doc, docData, getDoc, updateDoc, addDoc, query, where, orderBy, limit } from '@angular/fire/firestore';
import { Observable, of, from } from 'rxjs';
import { map, tap, retry, catchError, shareReplay } from 'rxjs/operators';
import { UnifiedShipment } from '../core/models/unified-shipment.models';

/**
 * 📦 SHIPMENT REPOSITORY SERVICE
 * ================================
 *
 * Service central pour toutes les opérations de données sur les shipments
 * - Pattern Repository pour abstraire l'accès à Firestore
 * - Cache intelligent pour optimiser les performances
 * - Retry logic pour la résilience
 * - Audit trail automatique
 * - Soft delete au lieu de hard delete
 *
 * @version 1.0.0
 * @author BeFret Development Team
 */
@Injectable({
  providedIn: 'root'
})
export class ShipmentRepositoryService {

  // Cache des shipments chargés (Map<trackingNumber, Observable<UnifiedShipment>>)
  private cache = new Map<string, Observable<UnifiedShipment | null>>();

  // TTL du cache (5 minutes)
  private readonly CACHE_TTL_MS = 5 * 60 * 1000;

  // Timestamps du cache pour gérer la fraîcheur
  private cacheTimestamps = new Map<string, number>();

  constructor(private firestore: Firestore) {
    console.log('✅ ShipmentRepositoryService initialized');
  }

  /**
   * Récupérer un shipment par trackingNumber avec cache intelligent
   */
  getByTrackingNumber(trackingNumber: string, forceRefresh = false): Observable<UnifiedShipment | null> {
    console.log(`🔍 Repository: Loading shipment ${trackingNumber}`);

    // Vérifier le cache si pas de force refresh
    if (!forceRefresh && this.isCacheValid(trackingNumber)) {
      console.log(`✅ Repository: Cache HIT for ${trackingNumber}`);
      return this.cache.get(trackingNumber)!;
    }

    console.log(`📡 Repository: Cache MISS for ${trackingNumber}, fetching from Firestore`);

    // Créer l'observable et le mettre en cache
    const q = query(
      collection(this.firestore, 'shipments'),
      where('trackingNumber', '==', trackingNumber)
    );
    const shipment$ = (collectionData(q, { idField: 'id' }) as Observable<UnifiedShipment[]>)
      .pipe(
        map(results => {
          if (results && results.length > 0) {
            console.log(`✅ Repository: Shipment found for ${trackingNumber}`);
            return results[0];
          }
          console.warn(`⚠️ Repository: Shipment NOT found for ${trackingNumber}`);
          return null;
        }),
        retry(3), // Retry 3 times en cas d'erreur réseau
        catchError(error => {
          console.error(`❌ Repository: Error loading shipment ${trackingNumber}:`, error);
          return of(null);
        }),
        shareReplay(1) // Partager le résultat entre plusieurs subscribers
      );

    // Mettre en cache
    this.cache.set(trackingNumber, shipment$);
    this.cacheTimestamps.set(trackingNumber, Date.now());

    return shipment$;
  }

  /**
   * Récupérer un shipment par document ID
   */
  getById(id: string): Observable<UnifiedShipment | null> {
    console.log(`🔍 Repository: Loading shipment by ID ${id}`);

    return (docData(doc(this.firestore, 'shipments', id)) as Observable<UnifiedShipment | undefined>)
      .pipe(
        map(shipment => {
          if (shipment) {
            console.log(`✅ Repository: Shipment found for ID ${id}`);
            return { ...shipment, id } as UnifiedShipment;
          }
          console.warn(`⚠️ Repository: Shipment NOT found for ID ${id}`);
          return null;
        }),
        retry(3),
        catchError(error => {
          console.error(`❌ Repository: Error loading shipment ID ${id}:`, error);
          return of(null);
        })
      );
  }

  /**
   * Récupérer tous les shipments d'un utilisateur
   */
  getByUserId(userId: string, limitCount = 50): Observable<UnifiedShipment[]> {
    console.log(`🔍 Repository: Loading shipments for user ${userId}`);

    const q = query(
      collection(this.firestore, 'shipments'),
      where('userId', '==', userId),
      where('metadata.flags.archived', '==', false),
      orderBy('timestamps.createdAt', 'desc'),
      limit(limitCount)
    );
    return (collectionData(q, { idField: 'id' }) as Observable<UnifiedShipment[]>)
      .pipe(
        tap(shipments => {
          console.log(`✅ Repository: Found ${shipments.length} shipments for user ${userId}`);
        }),
        retry(3),
        catchError(error => {
          console.error(`❌ Repository: Error loading shipments for user ${userId}:`, error);
          return of([]);
        })
      );
  }

  /**
   * Mettre à jour un shipment avec audit trail
   */
  async update(
    id: string,
    data: Partial<UnifiedShipment>,
    operator: string
  ): Promise<void> {
    console.log(`💾 Repository: Updating shipment ${id} by ${operator}`);

    try {
      // Préparer les données de mise à jour
      const updateData: any = {
        ...data,
        'timestamps.updatedAt': new Date().toISOString()
      };

      // Ajouter l'opérateur dans metadata si le champ existe
      if (data.metadata) {
        updateData['metadata.lastModifiedBy'] = operator;
      }

      // Mettre à jour dans Firestore
      await updateDoc(doc(this.firestore, 'shipments', id), updateData);

      console.log(`✅ Repository: Shipment ${id} updated successfully`);

      // Créer un log d'audit
      await this.createAuditLog({
        entity: 'shipment',
        entityId: id,
        action: 'update',
        operator,
        changes: data,
        timestamp: new Date().toISOString()
      });

      // Invalider le cache pour ce shipment
      const shipmentDoc = await getDoc(doc(this.firestore, 'shipments', id));

      const trackingNumber = (shipmentDoc.data() as UnifiedShipment | undefined)?.trackingNumber;
      if (trackingNumber) {
        this.invalidateCache(trackingNumber);
      }

    } catch (error) {
      console.error(`❌ Repository: Error updating shipment ${id}:`, error);
      throw error;
    }
  }

  /**
   * Supprimer un shipment (soft delete)
   */
  async delete(id: string, operator: string): Promise<void> {
    console.log(`🗑️ Repository: Soft deleting shipment ${id} by ${operator}`);

    try {
      // Soft delete: marquer comme archivé
      await updateDoc(doc(this.firestore, 'shipments', id), {
          'metadata.flags.archived': true,
          'metadata.flags.deletedAt': new Date().toISOString(),
          'metadata.deletedBy': operator,
          'timestamps.updatedAt': new Date().toISOString()
        });

      console.log(`✅ Repository: Shipment ${id} soft deleted successfully`);

      // Créer un log d'audit
      await this.createAuditLog({
        entity: 'shipment',
        entityId: id,
        action: 'delete',
        operator,
        timestamp: new Date().toISOString()
      });

      // Invalider le cache
      const shipmentDoc = await getDoc(doc(this.firestore, 'shipments', id));

      const trackingNumber = (shipmentDoc.data() as UnifiedShipment | undefined)?.trackingNumber;
      if (trackingNumber) {
        this.invalidateCache(trackingNumber);
      }

    } catch (error) {
      console.error(`❌ Repository: Error deleting shipment ${id}:`, error);
      throw error;
    }
  }

  /**
   * Restaurer un shipment soft deleted
   */
  async restore(id: string, operator: string): Promise<void> {
    console.log(`♻️ Repository: Restoring shipment ${id} by ${operator}`);

    try {
      await updateDoc(doc(this.firestore, 'shipments', id), {
          'metadata.flags.archived': false,
          'metadata.deletedBy': null,
          'metadata.flags.deletedAt': null,
          'metadata.restoredBy': operator,
          'timestamps.updatedAt': new Date().toISOString()
        });

      console.log(`✅ Repository: Shipment ${id} restored successfully`);

      await this.createAuditLog({
        entity: 'shipment',
        entityId: id,
        action: 'restore',
        operator,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error(`❌ Repository: Error restoring shipment ${id}:`, error);
      throw error;
    }
  }

  /**
   * Créer un log d'audit
   */
  private async createAuditLog(logData: any): Promise<void> {
    try {
      await addDoc(collection(this.firestore, 'audit_logs'), logData);
      console.log(`📝 Repository: Audit log created for ${logData.action} on ${logData.entityId}`);
    } catch (error) {
      // Ne pas bloquer l'opération principale si le log échoue
      console.warn(`⚠️ Repository: Failed to create audit log:`, error);
    }
  }

  /**
   * Vérifier si le cache est valide
   */
  private isCacheValid(trackingNumber: string): boolean {
    if (!this.cache.has(trackingNumber)) {
      return false;
    }

    const timestamp = this.cacheTimestamps.get(trackingNumber);
    if (!timestamp) {
      return false;
    }

    const age = Date.now() - timestamp;
    return age < this.CACHE_TTL_MS;
  }

  /**
   * Invalider le cache pour un tracking number
   */
  invalidateCache(trackingNumber: string): void {
    console.log(`🗑️ Repository: Invalidating cache for ${trackingNumber}`);
    this.cache.delete(trackingNumber);
    this.cacheTimestamps.delete(trackingNumber);
  }

  /**
   * Récupérer les logs de notifications pour un trackingNumber
   */
  getNotificationLogs(trackingNumber: string): Observable<any[]> {
    const q = query(
      collection(this.firestore, 'notification_results'),
      where('trackingNumber', '==', trackingNumber),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    return (collectionData(q, { idField: 'id' }) as Observable<any[]>)
      .pipe(
        catchError(error => {
          console.error(`❌ Repository: Error loading notification logs for ${trackingNumber}:`, error);
          return of([]);
        })
      );
  }

  /**
   * Vider tout le cache
   */
  clearCache(): void {
    console.log(`🗑️ Repository: Clearing all cache`);
    this.cache.clear();
    this.cacheTimestamps.clear();
  }
}
